import queryString from 'query-string';

import AppEnvironment from 'helpers/AppEnvironment';
import { logErrorAndLogToServer } from 'middleware/logger';

export type ErrorEventInfo = {
  status?: string | number;
  statusText?: string;
  url?: string;
  detailedMessage?: string;
  extraInformation?: string;
  message?: string;
};

function getErrorObjectMessage(error?: ErrorEventInfo | string) {
  if (typeof error === 'string') {
    return error;
  }
  return error ? error.detailedMessage || error.message || '' : '';
}

export function buildMessage(errorCode: string, type: string, errorEventInfo?: ErrorEventInfo | string) {
  return `${type || ''}${errorCode ? ` ${errorCode}` : ''}: ${getErrorObjectMessage(errorEventInfo)}`;
}

function fireZfcErrorTracker(msg: string) {
  if (window && typeof window.onerror === 'function') {
    window.onerror(msg);
  }
}

export function trackError(errorCode: string, type: string, errorEventInfo?: ErrorEventInfo | string, directedId?: string, stack?: string) {
  const formattedMessage = buildMessage(errorCode, type, errorEventInfo);
  const errorStack = stack || (typeof errorEventInfo === 'object' ? errorEventInfo?.extraInformation : undefined);
  const enrichedMessage = `${formattedMessage} | directedId=${directedId || 'unknown'} | stack=${errorStack || 'none'}`;
  if (AppEnvironment.hasZfc) {
    fireZfcErrorTracker(formattedMessage);
  }
  logErrorAndLogToServer(enrichedMessage);
}

export function buildErrorQueryStringForMartyPixel(errorCode = '', type = '', errorEventInfo: ErrorEventInfo, directedId?: string, stack?: string) {
  const { status = '', statusText = '', url = '', detailedMessage = '', extraInformation = '' } = errorEventInfo || {};
  const encodedDetailedMessage = encodeURIComponent(detailedMessage);
  // URL in this case is populated for FetchError type errors which indicate the api call that fetch url that errored.
  // The user's browser URL can be retrieved by looking at the referer header in splunk
  const encodedUrl = encodeURIComponent(url);
  const encodedExtraInformation = encodeURIComponent(extraInformation);
  const encodedStack = encodeURIComponent((stack || '').slice(0, 500));
  const encodedDirectedId = encodeURIComponent(directedId || '');
  return `errorType=${type}&errorCode=${errorCode}&extraInformation=${encodedExtraInformation}&status=${status}&detailedMessage=${encodedDetailedMessage}&url=${encodedUrl}&statusText=${statusText}&directedId=${encodedDirectedId}&stack=${encodedStack}`;
}

export function makeMartyPixelQueryString({ type, subType }: { type: string; subType: string }, details: object) {
  return `type=${type}${subType ? `&subType=${subType}` : ''}&${queryString.stringify(details)}`;
}

/** Builds a plain navigation AbortError (no timeout tag) — classified as `cancelled`. */
export function makeAbortError() {
  return Object.assign(new Error('The operation was aborted.'), { name: 'AbortError' });
}

/** Builds a timeout-tagged AbortError, mirroring what `withTimeout` aborts with — classified as `degraded`. */
export function makeTimeoutAbortError() {
  return Object.assign(new Error('Timed out'), { name: 'AbortError', isTimeout: true });
}

/**
 * Builds a client-side network TypeError — the shape `fetch()` rejects with when the
 * request cannot complete (lost connectivity, DNS failure, nav-away, bot abandon).
 * Recognized by `isNetworkError` and suppressed from ops failure metrics.
 * @param {string} [message] browser-dependent message (Chromium 'Failed to fetch',
 *   Safari 'Load failed', Firefox 'NetworkError when attempting to fetch resource.').
 */
export function makeNetworkError(message = 'Failed to fetch') {
  return new TypeError(message);
}
